import BasicFormWithDefault from "../../../components/textField/BasicFormWithDefault";
import { API_URL } from "../../../consts/ApiUrl";
import { validationMessage } from "../../../consts/ValidationMessage";
import { postUser } from "../../../utils/fetch";
import { encodePrivateKey } from "../../../utils/fileUtils";
import {
  ValidationErrors,
  validateRequiredFields,
} from "../../../utils/validationUtils";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import React, { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface VonageInfoTabProps {
  userData: Record<string, string>;
}

const VonageInfoTab: FC<VonageInfoTabProps> = ({ userData }) => {
  const navigate = useNavigate();
  const initialData = {
    applicationId: userData.ApplicationId || "",
    apiKey: userData.ApiKey || "",
    apiSecret: userData.ApiSecret || "",
    incomingWebhookUrl: userData.IncomingWebhookUrl || "",
  };

  const [formData, setFormData] = useState({ ...initialData });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFileName(file.name);
      setErrors((prev) => ({ ...prev, privateKey: "" }));
    } else {
      setUploadedFileName(null);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate: () => boolean = (): boolean => {
    const required: string = validationMessage.REQUIRED;
    const requiredFields: string[] = ["applicationId", "apiKey", "apiSecret"];
    const errorMessages = {
      applicationId: `アプリケーションID${required}`,
      apiKey: `API Key${required}`,
      apiSecret: `APIシークレット${required}`,
    };

    const validationErrors = validateRequiredFields(
      formData,
      requiredFields,
      errorMessages
    );
    if (!validationErrors.applicationId) {
      if (
        !/^[a-zA-Z0-9-]{36}$/.test(formData.applicationId) ||
        (formData.applicationId.match(/-/g) || []).length !== 4
      ) {
        validationErrors.applicationId = validationMessage.APP_ID_FORMAT;
      }
    }
    if (!validationErrors.apiKey) {
      if (!/^[a-zA-Z0-9]{8}$/.test(formData.apiKey)) {
        validationErrors.apiKey = validationMessage.API_KEY_FORMAT;
      }
    }
    if (!validationErrors.apiSecret) {
      // 文字数チェック（8文字から25文字）
      if (formData.apiSecret.length < 8 || formData.apiSecret.length > 25) {
        validationErrors.apiSecret = validationMessage.API_SECRET_FORMAT;
      }
      // 大文字が1つ以上含まれているかチェック
      else if (!/[A-Z]/.test(formData.apiSecret)) {
        validationErrors.apiSecret = validationMessage.API_SECRET_FORMAT;
      }
      // 小文字が1つ以上含まれているかチェック
      else if (!/[a-z]/.test(formData.apiSecret)) {
        validationErrors.apiSecret = validationMessage.API_SECRET_FORMAT;
      }
      // 数字が1つ以上含まれているかチェック
      else if (!/[0-9]/.test(formData.apiSecret)) {
        validationErrors.apiSecret = validationMessage.API_SECRET_FORMAT;
      }
    }
    const privateKeyFile = fileInputRef.current?.files?.[0] ?? null;
    if (!privateKeyFile) {
      validationErrors.privateKey = `秘密鍵${required}`;
    }
    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const handleSave = async (): Promise<void> => {
    if (!validate()) {
      return;
    }
    setIsLoading(true);
    try {
      const file = fileInputRef.current?.files?.[0] ?? null;
      const encodedPrivateKey = await encodePrivateKey(file);
      const updatedFields: Record<string, string> = Object.keys(
        formData
      ).reduce(
        (acc, key): Record<string, string> => {
          if (
            formData[key as keyof typeof formData] !==
            initialData[key as keyof typeof initialData]
          ) {
            acc[key] = formData[key as keyof typeof formData];
          }
          return acc;
        },
        {} as Record<string, string>
      );
      if (encodedPrivateKey) {
        updatedFields.privateKey = encodedPrivateKey;
      }
      if (Object.keys(updatedFields).length === 0) {
        setIsLoading(false);
        return;
      }
      const idToken: string = sessionStorage.getItem("idToken") || "";
      let result: any;
      if (process.env.DEV_SKIP_UNICON_UPGRADE_CHECK === 'true') {
        result = {
          status: "updated",
          message1: "",
          message2: "",
          link: "",
        };
      } else {
        const dataToSend = {
          api_key: formData.apiKey,
        };
        const response: Response = await fetch(API_URL.UNICON, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authentication: idToken,
          },
          body: JSON.stringify(dataToSend),
        });
        if (!response.ok) {
          navigate("/error");
        }
        const data = await response.json();
        result = data.result;
      }
      if (result.status !== "updated") {
        const message = `
            ${result.message1}
            <a href="${result.link}" target="_blank" rel="noopener noreferrer">こちら</a>
            ${result.message2}
          `;
        setErrors((prev) => ({ ...prev, apiKey: message }));
        setIsLoading(false);
        return;
      }
      const successPath: string = "/update/comp";
      updatedFields.operation = "update";
      await postUser(idToken, updatedFields, successPath, navigate);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      navigate("/error");
    } finally {
      setIsLoading(false);
    }
  };

  const plan: string = sessionStorage.getItem("plan") || "";
  if (!plan) {
    navigate("/signin");
  }

  if (plan === "standard") {
    return (
      <Box
        display="flex"
        flexDirection="column"
        gap={3}
        width="100%"
        maxWidth="400px"
      >
        <BasicFormWithDefault
          label="アプリケーションID"
          name="applicationId"
          value={formData.applicationId}
          defaultValue={userData.ApplicationId || ""}
          onChange={handleChange}
          error={!!errors.applicationId}
          helperText={errors.applicationId}
        />
        <BasicFormWithDefault
          label="API Key"
          name="apiKey"
          value={formData.apiKey}
          defaultValue={userData.ApiKey || ""}
          onChange={handleChange}
          error={!!errors.apiKey}
          helperText={
            errors.apiKey && (
              <span dangerouslySetInnerHTML={{ __html: errors.apiKey }} />
            )
          }
        />
        <BasicFormWithDefault
          label="APIシークレット"
          name="apiSecret"
          value={formData.apiSecret}
          defaultValue={userData.ApiSecret || ""}
          onChange={handleChange}
          error={!!errors.apiSecret}
          helperText={errors.apiSecret}
        />
        <BasicFormWithDefault
          label={"着信用 Webhook URL"}
          name={"incomingWebhookUrl"}
          value={formData.incomingWebhookUrl}
          defaultValue={userData.incomingWebhookUrl || ""}
          onChange={handleChange}
          isRequired={false}
        />
        <Box>
          <Typography variant="body1" gutterBottom color="textSecondary">
            秘密鍵
          </Typography>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{
              color: "text.secondary",
              borderColor: "rgba(0, 0, 0, 0.23)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                borderColor: "rgba(0, 0, 0, 0.23)",
              },
              textTransform: "none",
            }}
          >
            ファイルを選択
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={handleFileUpload}
            />
          </Button>
          {uploadedFileName && (
            <Typography variant="body2" color="textSecondary" mt={1}>
              アップロードされたファイル: {uploadedFileName}
            </Typography>
          )}
          {!!errors.privateKey && (
            <Typography variant="body2" color="error" mt={1}>
              {errors.privateKey}
            </Typography>
          )}
        </Box>
        <Box display="flex" justifyContent="center">
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSave}
            >
              情報更新
            </Button>
          )}
        </Box>
      </Box>
    );
  }
  return (
    <Box textAlign="center" mt={10} mb={20}>
      <Typography variant="body1">
        こちらはスタンダードプランの機能です
      </Typography>
    </Box>
  );
};

export default VonageInfoTab;
